// Runtime evaluation of field conditions (visibility / required logic).
// Mirrors the operator semantics defined in the editor's conditionsModel.ts.
// Used by FormBuilder.vue to decide isElementVisible / isElementRequired.
import type { FieldConditions, ConditionRule } from './types'

// A value counts as "empty" when it is null/undefined, an empty string, or an
// empty array (checkbox-group with nothing selected).
function isEmptyValue(value: unknown): boolean {
    if (value === null || value === undefined) return true
    if (Array.isArray(value)) return value.length === 0
    return value === ''
}

function toArray(value: unknown): string[] {
    if (Array.isArray(value)) return value.map((v) => String(v))
    if (value === null || value === undefined || value === '') return []
    return [String(value)]
}

// Evaluates a single rule against the current value of the referenced field.
export function matchRule(rule: ConditionRule, value: unknown): boolean {
    switch (rule.operator) {
        case 'is-empty':
            return isEmptyValue(value)
        case 'is-not-empty':
            return !isEmptyValue(value)
        case 'is-true':
            return value === true
        case 'is-false':
            return value === false
        case 'is-equal-to': {
            // value may be a single value OR a list (matches if equal to ANY).
            if (Array.isArray(rule.value)) {
                return rule.value.map(String).includes(String(value ?? ''))
            }
            return String(value ?? '') === String(rule.value ?? '')
        }
        case 'is-not-equal-to': {
            // with a list: matches if equal to NONE of the values.
            if (Array.isArray(rule.value)) {
                return !rule.value.map(String).includes(String(value ?? ''))
            }
            return String(value ?? '') !== String(rule.value ?? '')
        }
        case 'is-any-equal-to': {
            // checkbox-group: match if any selected value is in the rule set.
            const selected = toArray(value)
            const wanted = toArray(rule.value)
            return selected.some((v) => wanted.includes(v))
        }
        case 'is-not-any-equal-to': {
            const selected = toArray(value)
            const wanted = toArray(rule.value)
            return !selected.some((v) => wanted.includes(v))
        }
        default:
            return false
    }
}

// Evaluates a full conditions object against the map of current field values.
// Semantics: ALL `and` groups must match; a group matches if ANY of its `or`
// rules match. A rule-less / empty conditions object is treated as "no
// condition" → true (the field stays visible / keeps its static required flag).
export function evaluateConditions(
    conditions: FieldConditions | undefined | null,
    formFieldValues: Record<string, unknown>,
): boolean {
    if (!conditions || !Array.isArray(conditions.and) || conditions.and.length === 0) {
        return true
    }
    return conditions.and.every((group) => {
        const rules = (group.or || []).filter((r) => r && r.field)
        if (rules.length === 0) return true // an empty group is no constraint
        return rules.some((rule) => matchRule(rule, formFieldValues[rule.field]))
    })
}
